import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseForm } from 'projects/admin/src/app/classes/base-form';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { TemplateModel } from 'projects/core/src/lib/models/template.model';
import {
  faCheck,
} from '@fortawesome/pro-light-svg-icons';
import { SelectItem } from 'primeng/api';
import { LANGUAGES } from 'projects/core/src/lib/constants';
import { forkJoin } from 'rxjs';
import { RatingtypeModel } from 'projects/core/src/lib/models/ratingtype.model';
import { TranslocoService } from '@ngneat/transloco';
import { UserModel } from 'projects/core/src/lib/models/user.model';
import { HelperService } from 'projects/admin/src/app/services/helper.service';
import * as moment from 'moment-timezone';
import { FormControlComponent } from 'projects/admin/src/app/shared/form-control/form-control.component';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { CustomerModel } from 'projects/core/src/lib/models/customer.model';

@Component({
  selector: 'app-ac-new',
  templateUrl: './ac-new.component.html',
  styleUrls: ['./ac-new.component.scss']
})
export class AcNewComponent extends BaseForm implements OnInit, AfterViewInit {

  @ViewChild('name') name: FormControlComponent;

  public loading = false;
  public mode = 'planned';
  public templates: Array<TemplateModel> = [];
  public selected: TemplateModel = null;
  public customers: Array<CustomerModel> = [];
  public dispatchers: Array<UserModel> = [];
  public languages: Array<SelectItem> = [];
  public ratingtypes: Array<any> = [];
  public minDate = new Date();
  public country: string | null = null;
  public timezone: string | null = null;

  public faCheck = faCheck;

  public locationTypes: Array<SelectItem> = [
    {label: this.transloco.translate('common.labels.onsite'), value: 'onsite'},
    {label: this.transloco.translate('common.labels.virtual'), value: 'virtual'},
  ];

  public form: UntypedFormGroup = this.fb.group({
    name: new UntypedFormControl(null, [Validators.required]),
    customer: new UntypedFormControl(null),
    dispatcher: new UntypedFormControl(null, [Validators.required]),
    ratingtype: new UntypedFormControl(null, [Validators.required]),
    language: new UntypedFormControl(null, [Validators.required]),
    location_type: new UntypedFormControl(null),
    location_name: new UntypedFormControl(null),
    conference_url: new UntypedFormControl(null),
    conference_info: new UntypedFormControl(null),
    documents_visible_observer: new UntypedFormControl(false),
    start_time: new UntypedFormControl(null, [Validators.required]),
    tz: this.fb.group({})
  });

  constructor(
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private api: ApiService,
    private router: Router,
    private route: ActivatedRoute,
    private helper: HelperService,
    private auth: AuthStateService,
    public transloco: TranslocoService
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.auth.checkRightOrRedirect('admin.ac.create')) {
      this.route.params.subscribe(params => {
        if (params.mode) {
          this.mode = params.mode;
        }
      });
      this.form.disable();
      this.loading = true;
      this.languages = FormHelper.getSelectList(LANGUAGES, null, this.transloco);
      this.initFormBehaviour();
      forkJoin([
        this.api.getModels('templates'),
        this.api.getModels('ratingtypes'),
        this.api.getModels('users', {type: 'dispatcher'}),
        this.api.getModels('customers')
      ]).subscribe({
        next: (result: any) => {
          this.templates = [];
          result[0].data.forEach((template: any) => {
            const model = new TemplateModel().deserialize(template);
            if (model.is_template === true) {
              this.templates.push(model);
            }
          });
          this.templates.sort((a: TemplateModel, b: TemplateModel) => {
            return (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0);
          });
          this.templates.unshift(this.getIndividualTemplateData());
          this.ratingtypes = [];
          result[1].data.forEach((ratingtype: any) => {
            this.ratingtypes.push(new RatingtypeModel().deserialize(ratingtype));
          });
          if (result[2] !== null && result[2].data.length > 0) {
            result[2].data.forEach((dispatcher: any) => {
              this.dispatchers.push(new UserModel().deserialize(dispatcher));
            });
          }
          this.dispatchers.sort((a: UserModel, b: UserModel) => {
            return (a.lastname > b.lastname) ? 1 : ((b.lastname > a.lastname) ? -1 : 0);
          });
          if (result[3] !== null && result[3].data.length > 0) {
            result[3].data.forEach((customer: any) => {
              this.customers.push(new CustomerModel().deserialize(customer));
            });
          }
          this.customers.sort((a: CustomerModel, b: CustomerModel) => {
            return this.helper.getSortValue(a.name, b.name);
          });
        },
        error: (error: any) => {
        }
      }).add(() => {
        this.loading = false;
      });
    }
  }

  ngAfterViewInit(): void {
    this.form.disable();
  }

  onSubmit(): void {
    this.ui.hideMessage();
    if (this.form.valid === true) {
      this.submitting = true;
      const payload = {
        name: this.form.get('name').value,
        language: this.form.get('language').value,
        customer: this.form.get('customer').value?.id,
        dispatcher: this.form.get('dispatcher').value?.id,
        ratingtype: this.form.get('ratingtype').value?.id,
        location_type: this.form.get('location_type').value,
        location_name: this.form.get('location_name').value,
        country: this.country,
        timezone: this.timezone,
        conference_url: this.form.get('conference_url').value,
        conference_info: this.form.get('conference_info').value,
        documents_visible_observer: this.form.get('documents_visible_observer').value,
        start_time: moment(this.form.value.start_time).format('YYYY-MM-DD HH:mm:ss'),
        template: (this.selected?.id) ? this.selected.id : null
      };
      this.api.saveModel('acs', null, payload).subscribe({
        next: (result: any) => {
          this.ui.toastSuccess(this.transloco.translate('common.messages.saved', { value: this.form.value.name }), this.transloco.translate('common.saved'));
          this.router.navigate(['/acs/planned', result.data.id]);
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          FormHelper.setBackendErrors(this.form, error.fielderrors);
        }
      }).add(() => {
        this.submitting = false;
      });
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  onCancel(): void {
    this.router.navigate(['/acs', this.mode]);
  }

  onTemplateSelect(event: any): void {
    this.form.patchValue({
      name: event.data?.name,
      ratingtype: this.ratingtypes.find((ratingtype) => ratingtype.id === event.data?.ratingtype?.id),
      language: event.data?.language
    });
    if (event.data?.onsite === true && event.data?.virtual === false) {
      this.form.get('location_type').setValue('onsite');
    }
    if (event.data?.onsite === false && event.data?.virtual === true) {
      this.form.get('location_type').setValue('virtual');
    }
    this.form.enable();
    if (event.data?.id !== null) {
      this.form.get('ratingtype').disable();
      this.form.get('language').disable();
      setTimeout(() => {
        this.name.select();
      }, 0);
    } else {
      this.form.get('name').setValue('');
      this.form.get('ratingtype').enable();
      this.form.get('language').enable();
      setTimeout(() => {
        this.name.focus();
      }, 0);
    }
  }

  onTemplateUnselect(event: any): void {
    this.form.patchValue({
      name: null,
      ratingtype: null,
      language: null,
      location_type: null,
    });
    this.form.disable();
  }

  getDstLabel(): string {
    return ' (' + this.helper.getTimezoneName(this.timezone, this.form.get('start_time').value) + ')';
  }

  public isLocationTypeSet(): boolean {
    return this.form.get('location_type').value !== null;
  }

  public isVirtual(): boolean {
    return this.form.get('location_type').value === 'virtual';
  }

  private getIndividualTemplateData(): TemplateModel {
    return new TemplateModel().deserialize({
      id: null,
      name: this.transloco.translate('common.individual_procedure')
    });
  }

  private initFormBehaviour(): void {
    this.form.get('location_type').valueChanges.subscribe((value: string) => {
      if (value === 'onsite') {
        this.form.get('location_name').setValidators([Validators.required]);
      } else {
        this.form.get('location_name').clearValidators();
      }
      this.form.get('location_name').updateValueAndValidity();
    });
  }

}
